import { readonly, ref } from 'vue';
import { useNotificationsStore } from '~/stores/notifications';
import { NotificationType } from '~/types/notifications';

/**
 * 通知輪詢策略配置
 */
interface PollingStrategy {
  maxAttempts: number;
  delay: number; // 毫秒
  maxDuration: number; // 毫秒
}

/**
 * 各通知類型的輪詢策略配置
 * 根據文檔建議的輪詢策略設置
 */
const POLLING_STRATEGIES: Record<string, PollingStrategy> = {
  // 2FA 啟用/禁用：1 分鐘，5 秒間隔，12 次
  [NotificationType.TwoFactorEnabled]: {
    maxAttempts: 12,
    delay: 5000,
    maxDuration: 60 * 1000, // 1 分鐘
  },
  [NotificationType.TwoFactorDisabled]: {
    maxAttempts: 12,
    delay: 5000,
    maxDuration: 60 * 1000, // 1 分鐘
  },
  // 密碼重置成功：1 分鐘，5 秒間隔，12 次
  [NotificationType.PasswordResetSuccess]: {
    maxAttempts: 12,
    delay: 5000,
    maxDuration: 60 * 1000, // 1 分鐘
  },
  // 支付成功/失敗：3 分鐘，10 秒間隔，18 次
  [NotificationType.PaymentSuccess]: {
    maxAttempts: 18,
    delay: 10000,
    maxDuration: 3 * 60 * 1000, // 3 分鐘
  },
  [NotificationType.PaymentFailed]: {
    maxAttempts: 18,
    delay: 10000,
    maxDuration: 3 * 60 * 1000, // 3 分鐘
  },
  // 訂閱創建/取消：2 分鐘，10 秒間隔，12 次
  [NotificationType.SubscriptionCreated]: {
    maxAttempts: 12,
    delay: 10000,
    maxDuration: 2 * 60 * 1000, // 2 分鐘
  },
  [NotificationType.SubscriptionCancelled]: {
    maxAttempts: 12,
    delay: 10000,
    maxDuration: 2 * 60 * 1000, // 2 分鐘
  },
  // 錢包充值：2 分鐘，10 秒間隔，12 次
  [NotificationType.WalletRecharged]: {
    maxAttempts: 12,
    delay: 10000,
    maxDuration: 2 * 60 * 1000, // 2 分鐘
  },
  // AI 應用發布：5 分鐘，15 秒間隔，20 次
  [NotificationType.AiAppPublished]: {
    maxAttempts: 20,
    delay: 15000,
    maxDuration: 5 * 60 * 1000, // 5 分鐘
  },
  // 資料源處理完成/失敗：5 分鐘，15 秒間隔，20 次
  [NotificationType.DatasourceProcessed]: {
    maxAttempts: 20,
    delay: 15000,
    maxDuration: 5 * 60 * 1000, // 5 分鐘
  },
  [NotificationType.DatasourceFailed]: {
    maxAttempts: 20,
    delay: 15000,
    maxDuration: 5 * 60 * 1000, // 5 分鐘
  },
  // OAuth 授權/撤銷：1 分鐘，5 秒間隔，12 次
  [NotificationType.OauthAppAuthorized]: {
    maxAttempts: 12,
    delay: 5000,
    maxDuration: 60 * 1000, // 1 分鐘
  },
  [NotificationType.OauthAppRevoked]: {
    maxAttempts: 12,
    delay: 5000,
    maxDuration: 60 * 1000, // 1 分鐘
  },
  // Feedback 補丁生成完成/失敗：5 分鐘，15 秒間隔，20 次
  [NotificationType.FeedbackPatchReady]: {
    maxAttempts: 20,
    delay: 15000,
    maxDuration: 5 * 60 * 1000, // 5 分鐘
  },
  [NotificationType.FeedbackPatchFailed]: {
    maxAttempts: 20,
    delay: 15000,
    maxDuration: 5 * 60 * 1000, // 5 分鐘
  },
};

/**
 * 獲取預設輪詢策略（如果沒有配置）
 */
const getDefaultStrategy = (): PollingStrategy => ({
  maxAttempts: 18,
  delay: 10000,
  maxDuration: 3 * 60 * 1000, // 預設 3 分鐘
});

/**
 * 通知輪詢 Composable
 * 用於在主動操作後輪詢檢查是否收到對應的通知
 */
export function useNotificationPolling() {
  const notificationsStore = useNotificationsStore();

  // 輪詢狀態
  const pollingInterval = ref<NodeJS.Timeout | null>(null);
  const pollingAttempts = ref(0);
  const pollingStartTime = ref<number>(0);
  const isPolling = ref(false);

  /**
   * 清理輪詢
   */
  const clearPolling = () => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value);
      pollingInterval.value = null;
    }
    pollingAttempts.value = 0;
    isPolling.value = false;
  };

  /**
   * 檢查通知
   * @param expectedType 期望的通知類型
   * @param strategy 輪詢策略
   * @returns 是否找到通知
   */
  const checkNotification = async (
    expectedType: string,
    strategy: PollingStrategy
  ): Promise<boolean> => {
    // 如果已經停止輪詢，直接返回
    if (!isPolling.value) {
      return false;
    }

    try {
      // 獲取最新通知（只獲取前 5 條，快速檢查）
      await notificationsStore.fetchNotifications({ page: 1, page_size: 5 });

      // 檢查是否有對應類型的新通知（在輪詢開始時間之後創建的）
      // created_at 是 UTC+0 時間戳（毫秒），例如：1768371644844
      // Date.now() 也是毫秒級時間戳（UTC），兩者可以直接比較
      // 為了避免時間同步或網路延遲問題，使用 3 秒緩衝時間
      const bufferTime = 3000; // 3 秒緩衝，避免時間同步問題
      const hasNewNotification = notificationsStore.notifications.some((n) => {
        // created_at 是毫秒級時間戳（13 位數），直接使用
        // 使用緩衝時間，確保不會因為時間同步問題而錯過通知
        return n.type === expectedType && n.created_at > pollingStartTime.value - bufferTime;
      });

      if (hasNewNotification) {
        // 找到通知後，刷新完整的通知列表和未讀數量
        await notificationsStore.fetchNotifications({
          page: 1,
          page_size: notificationsStore.pagination.page_size,
        });
        await notificationsStore.fetchUnreadCount();

        clearPolling();
        return true; // 找到通知，停止輪詢
      }

      pollingAttempts.value++;

      // 檢查是否達到限制
      const elapsed = Date.now() - pollingStartTime.value;
      if (pollingAttempts.value >= strategy.maxAttempts || elapsed >= strategy.maxDuration) {
        clearPolling();
        return false; // 達到限制，停止輪詢
      }

      return false; // 繼續輪詢
    } catch (error) {
      console.error('檢查通知失敗:', error);
      pollingAttempts.value++;

      // 錯誤時也檢查限制
      if (pollingAttempts.value >= strategy.maxAttempts) {
        clearPolling();
      }
      return false;
    }
  };

  /**
   * 開始輪詢通知
   * @param expectedType 期望的通知類型（使用 NotificationType enum）
   * @param customStrategy 可選的自定義輪詢策略（如果不提供，使用預設策略）
   * @returns Promise<void>
   */
  const startPollingNotification = async (
    expectedType: NotificationType | string,
    customStrategy?: PollingStrategy
  ): Promise<void> => {
    // 如果已經在輪詢，先清理
    if (isPolling.value) {
      clearPolling();
    }

    // 獲取輪詢策略
    const strategy = customStrategy || POLLING_STRATEGIES[expectedType] || getDefaultStrategy();

    pollingStartTime.value = Date.now();
    pollingAttempts.value = 0;
    isPolling.value = true;

    // 立即檢查一次
    await checkNotification(expectedType, strategy);

    // 如果已經找到通知，直接返回
    if (!isPolling.value) {
      return;
    }

    // 設定定期輪詢
    pollingInterval.value = setInterval(async () => {
      // 檢查是否還在輪詢中（可能已經被其他邏輯停止）
      if (!isPolling.value) {
        clearPolling();
        return;
      }
      await checkNotification(expectedType, strategy);
    }, strategy.delay);
  };

  /**
   * 停止輪詢（手動停止）
   */
  const stopPolling = () => {
    clearPolling();
  };

  return {
    startPollingNotification,
    stopPolling,
    clearPolling,
    isPolling: readonly(isPolling),
  };
}
